/**
 * BikeComparisonMetricCharts — dual-line charts for speed, power, gradient.
 */
import { useMemo, useState } from 'react';
import {
  ResponsiveContainer,
  ComposedChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';
import { Box, Typography, ToggleButtonGroup, ToggleButton, useTheme, useMediaQuery } from '@mui/material';
import { tokens } from '../../../theme';

const COLOR_A = '#3b82f6';
const COLOR_B = '#f59e0b';

const METRICS = [
  { id: 'speed', label: 'Speed', keyA: 'speed_a', keyB: 'speed_b', unit: 'km/h', decimals: 1 },
  { id: 'power', label: 'Power', keyA: 'power_a', keyB: 'power_b', unit: 'W', decimals: 0 },
  { id: 'gradient', label: 'Gradient', keyA: 'gradient_a', keyB: 'gradient_b', unit: '%', decimals: 1 },
];

function MetricChart({ data, metric, colors, isMobile }) {
  if (!data.length) return null;

  const hasData = data.some(r => r[metric.keyA] != null || r[metric.keyB] != null);
  if (!hasData) return null;

  const fmt = (v) => v == null ? '—' : `${Number(v).toFixed(metric.decimals)} ${metric.unit}`;

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null;
    return (
      <Box sx={{ bgcolor: '#1e293b', border: '1px solid #334155', borderRadius: 1, p: 1.5, fontSize: '0.8rem' }}>
        <Typography variant="caption" color="#94a3b8">{`${(label / 1000).toFixed(1)} km`}</Typography>
        <Box mt={0.5}>
          {payload.map((p) => (
            <Typography key={p.dataKey} variant="body2" color={p.color}>
              {p.name}: {fmt(p.value)}
            </Typography>
          ))}
        </Box>
      </Box>
    );
  };

  return (
    <Box mb={3}>
      <Typography variant="caption" color={colors.grey[400]} mb={1} display="block">
        {metric.label} ({metric.unit})
      </Typography>
      <ResponsiveContainer width="100%" height={200}>
        <ComposedChart data={data} margin={isMobile ? { top: 5, right: 5, bottom: 5, left: 0 } : { top: 5, right: 20, bottom: 10, left: 20 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={colors.grey[700]} />
          <XAxis
            dataKey="course_distance_m"
            tickFormatter={(v) => `${(v / 1000).toFixed(0)}`}
            tick={{ fill: colors.grey[400], fontSize: isMobile ? 9 : 10 }}
          />
          <YAxis
            tick={isMobile ? false : { fill: colors.grey[400], fontSize: 10 }}
            width={isMobile ? 0 : 50}
            axisLine={!isMobile}
            tickLine={!isMobile}
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend wrapperStyle={{ fontSize: '0.75rem' }} />
          <Line type="monotone" dataKey={metric.keyA} name="A" stroke={COLOR_A} dot={false} strokeWidth={2} connectNulls />
          <Line type="monotone" dataKey={metric.keyB} name="B" stroke={COLOR_B} dot={false} strokeWidth={2} connectNulls />
        </ComposedChart>
      </ResponsiveContainer>
    </Box>
  );
}

export default function BikeComparisonMetricCharts({ resampled }) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [selected, setSelected] = useState(['speed', 'power']);

  const data = useMemo(() => resampled || [], [resampled]);

  if (!data.length) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" height={200}>
        <Typography color={colors.grey[400]}>No data available</Typography>
      </Box>
    );
  }

  const handleToggle = (_, newSelected) => {
    if (newSelected.length > 0) setSelected(newSelected);
  };

  return (
    <Box>
      <Box mb={2}>
        <ToggleButtonGroup value={selected} onChange={handleToggle} size="small">
          {METRICS.map(m => (
            <ToggleButton key={m.id} value={m.id} sx={{ fontSize: '0.75rem' }}>
              {m.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>
      {METRICS.filter(m => selected.includes(m.id)).map(metric => (
        <MetricChart key={metric.id} data={data} metric={metric} colors={colors} isMobile={isMobile} />
      ))}
    </Box>
  );
}
